import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { APIError } from "@/api/utils/errors";
import { queryClient } from "@/lib/query/queryClient";

import { FilesystemAPI } from "../api/filesystemApi";
import { computeExhibitLabels } from "../exhibitLabels";
import { applyOptimisticMove, buildTree } from "../navigation";
import type { DocumentRole, ExhibitScheme, FsNode, MoveTarget } from "../types";
import { DEFAULT_EXHIBIT_SCHEME } from "../types";

/**
 * Owns the case filesystem for the browser: the nodes, the exhibit scheme, and
 * every mutation.
 *
 * The whole tree is fetched at once rather than a column at a time. Exhibit
 * numbering is a function of the entire tree, so a change anywhere can renumber
 * anything; holding only the visible columns would mean the labels on screen
 * could not be trusted after an edit.
 *
 * The fetch is a TanStack query, retried with backoff on network and server
 * errors: a cold load that hits a blip reads "Reconnecting…" and recovers on
 * its own instead of showing an empty case. Mutations stay optimistic on the
 * local copy; a fetch that started before one is not applied over it.
 */

export type CaseFilesystem = {
  nodes: FsNode[];
  scheme: ExhibitScheme;
  /** Optimistic labels, recomputed locally on every mutation. */
  labels: Record<string, string | null>;
  isLoading: boolean;
  /** The fetch failed and is being retried; not an empty case. */
  reconnecting: boolean;
  /** The tree has loaded at least once, so an empty list means no files. */
  loaded: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  createFolder: (
    name: string,
    parentId: string | null
  ) => Promise<FsNode | null>;
  rename: (nodeId: string, name: string) => Promise<void>;
  setPacketInclusion: (nodeId: string, included: boolean) => Promise<void>;
  setExhibitNumbering: (nodeId: string, numbered: boolean) => Promise<void>;
  setDocumentRole: (nodeIds: string[], role: DocumentRole) => Promise<void>;
  /** Category and type set by hand; throws (after undoing) when not saved. */
  setClassification: (changes: ClassificationChange[]) => Promise<void>;
  move: (nodeIds: string[], target: MoveTarget) => Promise<void>;
  remove: (nodeIds: string[]) => Promise<void>;
  saveScheme: (scheme: ExhibitScheme) => Promise<void>;
  addNodes: (nodes: FsNode[]) => void;
};

/** Files that get one category and document type (null clears). */
export type ClassificationChange = {
  nodeIds: string[];
  category: string | null;
  documentType: string | null;
};

const mergeNodes = (current: FsNode[], updated: FsNode[]): FsNode[] => {
  const byId = new Map(updated.map((node) => [node.id, node]));
  const merged = current.map((node) => byId.get(node.id) ?? node);
  updated.forEach((node) => {
    if (!current.some((existing) => existing.id === node.id)) merged.push(node);
  });
  return merged;
};

export const caseTreeQueryKey = (individualId: string) =>
  ["case-fs-tree", individualId] as const;

type TreeResponse = { nodes: FsNode[]; exhibit_scheme: ExhibitScheme };
type TreeFetch = TreeResponse & { startedAt: number };

const MAX_TREE_RETRIES = 5;

/** Network failures (no response), timeouts, throttling and 5xx are worth another try. */
export const isTransientTreeError = (error: unknown): boolean => {
  if (!(error instanceof APIError)) return true;
  return error.status >= 500 || error.status === 408 || error.status === 429;
};

export const treeRetryDelay = (attempt: number): number =>
  Math.min(1_000 * 2 ** attempt, 15_000);

export const useCaseFilesystem = (individualId: string): CaseFilesystem => {
  // Bumped by every local mutation: a fetch that started before the latest
  // one is not applied over its optimistic state.
  const requestId = useRef(0);
  const cached = queryClient.getQueryData<TreeFetch>(
    caseTreeQueryKey(individualId)
  );
  const [nodes, setNodes] = useState<FsNode[]>(() => cached?.nodes ?? []);
  const [scheme, setScheme] = useState<ExhibitScheme>(
    () => cached?.exhibit_scheme ?? DEFAULT_EXHIBIT_SCHEME
  );
  const [syncedFor, setSyncedFor] = useState<string | null>(() =>
    cached ? individualId : null
  );
  const [error, setError] = useState<string | null>(null);

  // A case switch starts from that case's cache or nothing: a failed first
  // read must never leave the previous case's files on screen.
  const [loadedFor, setLoadedFor] = useState(individualId);
  if (loadedFor !== individualId) {
    setLoadedFor(individualId);
    setNodes(cached?.nodes ?? []);
    setScheme(cached?.exhibit_scheme ?? DEFAULT_EXHIBIT_SCHEME);
    setSyncedFor(cached ? individualId : null);
    setError(null);
  }

  const query = useQuery(
    {
      queryKey: caseTreeQueryKey(individualId),
      queryFn: async (): Promise<TreeFetch> => {
        const startedAt = requestId.current;
        const response = await FilesystemAPI.getTree(individualId);
        return { ...response, startedAt };
      },
      retry: (failures, caught) =>
        failures < MAX_TREE_RETRIES && isTransientTreeError(caught),
      retryDelay: treeRetryDelay,
      refetchOnWindowFocus: false
    },
    queryClient
  );

  useEffect(() => {
    const { data } = query;
    if (!data || data.startedAt !== requestId.current) return;
    setNodes(data.nodes);
    setScheme(data.exhibit_scheme ?? DEFAULT_EXHIBIT_SCHEME);
    setSyncedFor(individualId);
    setError(null);
  }, [query.data, query.dataUpdatedAt, individualId]);

  const loaded = syncedFor === individualId;
  const reconnecting = query.isFetching && query.failureCount > 0;
  const fetchError =
    query.isError && !query.isFetching
      ? query.error instanceof Error
        ? query.error.message
        : "Could not load case files"
      : null;

  const { refetch } = query;
  const refresh = useCallback(async () => {
    await refetch({ cancelRefetch: true });
  }, [refetch]);

  /**
   * Applies a local change immediately, then reconciles with the server. On
   * failure the previous nodes are restored, because a half-applied move would
   * leave the visible exhibit numbers wrong.
   */
  const mutate = useCallback(
    async (
      optimistic: (current: FsNode[]) => FsNode[],
      commit: () => Promise<void>
    ) => {
      const previous = nodes;
      setNodes(optimistic(previous));
      requestId.current += 1;
      try {
        await commit();
        setError(null);
      } catch (caught) {
        setNodes(previous);
        setError(
          caught instanceof Error ? caught.message : "Change could not be saved"
        );
      }
    },
    [nodes]
  );

  const patchLocal = useCallback(
    (nodeId: string, updates: Partial<FsNode>) => (current: FsNode[]) =>
      current.map((node) =>
        node.id === nodeId ? { ...node, ...updates } : node
      ),
    []
  );

  const retitledLocal = useCallback(
    (nodeId: string, name: string) => (current: FsNode[]) =>
      current.map((node) => {
        if (node.id !== nodeId) return node;
        if (node.kind === "folder") return { ...node, name, title: name };
        const filename = node.format ? `${name}.${node.format}` : name;
        return { ...node, name, title: name, filename };
      }),
    []
  );

  const createFolder = useCallback(
    async (name: string, parentId: string | null) => {
      try {
        const response = await FilesystemAPI.createFolder(
          individualId,
          name,
          parentId
        );
        setNodes((current) => mergeNodes(current, [response.node]));
        return response.node;
      } catch (caught) {
        setError(
          caught instanceof Error ? caught.message : "Could not create folder"
        );
        return null;
      }
    },
    [individualId]
  );

  const rename = useCallback(
    (nodeId: string, name: string) =>
      mutate(retitledLocal(nodeId, name), async () => {
        const response = await FilesystemAPI.patchNode(individualId, nodeId, {
          name
        });
        setNodes((current) => mergeNodes(current, [response.node]));
      }),
    [individualId, mutate, retitledLocal]
  );

  const setPacketInclusion = useCallback(
    (nodeId: string, included: boolean) => {
      const packet_inclusion = included ? "included" : "excluded";
      return mutate(patchLocal(nodeId, { packet_inclusion }), async () => {
        await FilesystemAPI.patchNode(individualId, nodeId, {
          packet_inclusion
        });
      });
    },
    [individualId, mutate, patchLocal]
  );

  const setExhibitNumbering = useCallback(
    (nodeId: string, numbered: boolean) => {
      const exhibit_numbering = numbered ? "numbered" : "unnumbered";
      return mutate(patchLocal(nodeId, { exhibit_numbering }), async () => {
        await FilesystemAPI.patchNode(individualId, nodeId, {
          exhibit_numbering
        });
      });
    },
    [individualId, mutate, patchLocal]
  );

  const setDocumentRole = useCallback(
    (nodeIds: string[], role: DocumentRole) => {
      const ids = new Set(nodeIds);
      return mutate(
        (current) =>
          current.map((node) =>
            ids.has(node.id)
              ? {
                  ...node,
                  document_role: role,
                  document_role_source: "attorney",
                  document_role_suggestion: null,
                  document_role_suggestion_reason: null,
                  ...(role === "internal"
                    ? { packet_inclusion: "excluded" as const }
                    : {})
                }
              : node
          ),
        async () => {
          const response = await FilesystemAPI.setDocumentRole(
            individualId,
            nodeIds,
            role
          );
          setNodes((current) => mergeNodes(current, response.nodes));
        }
      );
    },
    [individualId, mutate]
  );

  const setClassification = useCallback(
    async (changes: ClassificationChange[]) => {
      const byId = new Map(
        changes.flatMap((change) =>
          change.nodeIds.map((id) => [id, change] as const)
        )
      );
      const previous = nodes;
      setNodes((current) =>
        current.map((node) => {
          const change = byId.get(node.id);
          return change && node.kind === "file"
            ? {
                ...node,
                taxonomy_category: change.category,
                document_type: change.category ? change.documentType : null,
                classified_by: "attorney"
              }
            : node;
        })
      );
      requestId.current += 1;
      try {
        const responses = await Promise.all(
          changes.map((change) =>
            FilesystemAPI.setClassification(
              individualId,
              change.nodeIds,
              change.category,
              change.documentType
            )
          )
        );
        setNodes((current) =>
          mergeNodes(
            current,
            responses.flatMap((response) => response.nodes)
          )
        );
      } catch (caught) {
        setNodes(previous);
        throw caught;
      }
    },
    [individualId, nodes]
  );

  const move = useCallback(
    (nodeIds: string[], target: MoveTarget) =>
      mutate(
        (current) =>
          applyOptimisticMove(current, nodeIds, target.target_parent_id, {
            beforeId: target.before_id,
            afterId: target.after_id
          }),
        async () => {
          const response = await FilesystemAPI.moveNodes(
            individualId,
            nodeIds,
            target.target_parent_id,
            { beforeId: target.before_id, afterId: target.after_id }
          );
          // The server owns the real fractional keys; take them so the
          // provisional ones from the optimistic pass do not linger.
          setNodes((current) => mergeNodes(current, response.nodes));
        }
      ),
    [individualId, mutate]
  );

  const remove = useCallback(
    (nodeIds: string[]) => {
      const doomed = new Set(nodeIds);
      // A deleted folder takes its subtree with it, so mark descendants too.
      const tree = buildTree(nodes);
      const collect = (id: string) => {
        (tree.childrenByParent.get(id) ?? []).forEach((child) => {
          doomed.add(child.id);
          collect(child.id);
        });
      };
      nodeIds.forEach(collect);

      return mutate(
        (current) => current.filter((node) => !doomed.has(node.id)),
        async () => {
          await FilesystemAPI.deleteNodes(individualId, nodeIds);
        }
      );
    },
    [individualId, mutate, nodes]
  );

  const saveScheme = useCallback(
    async (next: ExhibitScheme) => {
      const previous = scheme;
      setScheme(next);
      try {
        await FilesystemAPI.putExhibitScheme(individualId, next);
        setError(null);
      } catch (caught) {
        setScheme(previous);
        setError(
          caught instanceof Error ? caught.message : "Could not save numbering"
        );
      }
    },
    [individualId, scheme]
  );

  const addNodes = useCallback((created: FsNode[]) => {
    setNodes((current) => mergeNodes(current, created));
  }, []);

  const labels = useMemo(
    () => computeExhibitLabels(nodes, scheme),
    [nodes, scheme]
  );

  return {
    nodes,
    scheme,
    labels,
    isLoading: query.isFetching || (!loaded && !fetchError),
    reconnecting,
    loaded,
    error: error ?? fetchError,
    refresh,
    createFolder,
    rename,
    setPacketInclusion,
    setExhibitNumbering,
    setDocumentRole,
    setClassification,
    move,
    remove,
    saveScheme,
    addNodes
  };
};
